/**
 * Shared hook for admin form pages (create/edit).
 *
 * Encapsulates the common pattern used by TenantFormPage, UserFormPage,
 * ModuleFormPage, RegistryFormPage, EndpointFormPage, and ErrorMappingFormPage:
 *
 * 1. Determine create vs edit mode from a route parameter
 * 2. Build an EntityFormConfig via a factory function
 * 3. Optionally fetch entity data for edit mode
 * 4. Map fetched data to initial form values
 * 5. Set up navigation, mutation, validation, and submit logic
 * 6. Return a patchedForm with custom submit/cancel and all state needed for rendering
 */

import { useMemo, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@components/integrated/entity-form';
import type { EntityFormConfig, UseEntityFormReturn } from '@components/integrated/entity-form';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseAdminFormPageOptions<TEntity, TParams extends object = Record<string, string | undefined>> {
  /**
   * Human-readable entity name used in error messages (e.g. 'tenant', 'user').
   */
  entityName: string;

  /**
   * Factory that builds the EntityFormConfig for the given mode.
   * Receives the route params so it can pass IDs/slugs to the config.
   */
  getConfig: (mode: 'create' | 'edit', params: TParams) => EntityFormConfig;

  /**
   * Determines whether we are in edit mode based on route params.
   * Returns the entity identifier string if edit mode, or `undefined` if create mode.
   */
  getEditId: (params: TParams) => string | undefined;

  /**
   * Query key for fetching the entity in edit mode.
   */
  queryKey: (params: TParams) => readonly unknown[];

  /**
   * Function to fetch the entity for edit mode.
   * Called only when `getEditId` returns a defined value.
   * Should throw/reject if the params are invalid.
   */
  fetchFn: (params: TParams) => Promise<{ data: TEntity }>;

  /**
   * Maps the fetched entity to initial form values.
   * Only called when entity data is available.
   */
  mapToInitialValues: (entity: TEntity) => Record<string, unknown>;

  /**
   * Mutation function called on form submit.
   * Receives the form values, whether we're editing, and the route params.
   */
  mutationFn: (
    payload: Record<string, unknown>,
    isEdit: boolean,
    params: TParams,
  ) => Promise<unknown>;

  /**
   * Query key prefix to invalidate on successful mutation.
   * The full invalidation key is built from this.
   */
  invalidateKey: (params: TParams) => readonly unknown[];

  /**
   * Path to navigate to after successful save or cancel.
   */
  navigateBackPath: (params: TParams) => string;

  /**
   * Whether the fetch query is enabled.
   * Defaults to `isEdit` when not provided.
   * Useful for endpoints/error-mappings that need extra conditions.
   */
  fetchEnabled?: (isEdit: boolean, params: TParams) => boolean;
}

export interface UseAdminFormPageReturn {
  /** Whether we are editing an existing entity. */
  isEdit: boolean;
  /** The form mode: 'create' or 'edit'. */
  mode: 'create' | 'edit';
  /** The resolved EntityFormConfig. */
  config: EntityFormConfig;
  /** Whether the entity data is currently being fetched (edit mode). */
  isFetchLoading: boolean;
  /** Fetch error, if any (edit mode). */
  fetchError: Error | null;
  /** Submit error message, if any. */
  submitError: string | null;
  /** Clear the submit error. */
  setSubmitError: (error: string | null) => void;
  /** The patched form object with custom submit and cancel. */
  patchedForm: UseEntityFormReturn;
  /** Route params (typed as generic record). */
  params: Record<string, string | undefined>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAdminFormPage<
  TEntity,
  TParams extends object = Record<string, string | undefined>,
>(
  options: UseAdminFormPageOptions<TEntity, TParams>,
): UseAdminFormPageReturn {
  const {
    entityName,
    getConfig,
    getEditId,
    queryKey,
    fetchFn,
    mapToInitialValues,
    mutationFn,
    invalidateKey,
    navigateBackPath,
    fetchEnabled,
  } = options;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams() as TParams;

  const editId = getEditId(params);
  const isEdit = editId !== undefined;
  const mode = isEdit ? 'edit' : 'create';

  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---- Form config ---------------------------------------------------------

  const config = useMemo(
    () => getConfig(mode, params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, ...Object.values(params)],
  );

  // ---- Fetch entity data for edit mode -------------------------------------

  const enabled = fetchEnabled ? fetchEnabled(isEdit, params) : isEdit;

  const {
    data: entityResponse,
    isLoading: isFetchLoading,
    error: fetchError,
  } = useQuery({
    queryKey: queryKey(params),
    queryFn: () => fetchFn(params),
    enabled,
  });

  // ---- Prepare initial values for edit mode --------------------------------

  const initialValues = useMemo(() => {
    if (!isEdit || !entityResponse?.data) return undefined;
    return mapToInitialValues(entityResponse.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, entityResponse]);

  // ---- Navigation handlers -------------------------------------------------

  const backPath = navigateBackPath(params);

  const navigateToList = useCallback(() => {
    navigate(backPath);
  }, [navigate, backPath]);

  // ---- Form hook (controlled mode) -----------------------------------------

  const form = useEntityForm({
    config,
    initialValues,
    mode,
    onSuccess: () => { navigateToList(); },
    onError: (err: unknown) => {
      if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError(`An unexpected error occurred while saving the ${entityName}.`);
      }
    },
    onCancel: navigateToList,
  });

  // ---- Override the default submit to use our mutation function -------------

  const customSubmit = useCallback(async () => {
    setSubmitError(null);

    const isValid = await form.validateForm();
    if (!isValid) return;

    try {
      await mutationFn(form.values, isEdit, params);
      void queryClient.invalidateQueries({ queryKey: invalidateKey(params) });
      navigateToList();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError(`An unexpected error occurred while saving the ${entityName}.`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, mutationFn, navigateToList, queryClient, isEdit, ...Object.values(params)]);

  // Create a patched form object with our custom submit and cancel
  const patchedForm = useMemo(
    () => ({ ...form, submit: customSubmit, cancel: navigateToList }),
    [form, customSubmit, navigateToList],
  );

  return {
    isEdit,
    mode,
    config,
    isFetchLoading,
    fetchError: fetchError ?? null,
    submitError,
    setSubmitError,
    patchedForm,
    params: params as Record<string, string | undefined>,
  };
}
